import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiAlertTriangle, FiArrowLeft, FiBell, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { NOTIFICATIONS_STATUS, useNotifications } from "../context/NotificationsContext";
import { formatNotificationDate } from "../helpers/notificationsHelper";
import { AnnouncementBody } from "./AnnouncementModal";
import "../styles/AnnouncementModal.css";
import "../styles/NotificationBell.css";

export const getBellLabel = (t, unreadCount) => {
  if (unreadCount <= 0) return t("notifications.bellLabel");
  if (unreadCount === 1) return t("notifications.bellLabelUnreadOne");
  return t("notifications.bellLabelUnread").replace("{count}", unreadCount);
};

const byNewest = (items) =>
  items
    .map((item, index) => ({ item, index, time: Date.parse(item.publishedAt) || 0 }))
    .sort((a, b) => b.time - a.time || a.index - b.index)
    .map(({ item }) => item);

const SEVERITY_KEYS = {
  warning: "notifications.severityWarning",
  critical: "notifications.severityCritical",
};

const SeverityBadge = ({ severity, t }) => {
  const key = SEVERITY_KEYS[severity];
  if (!key) return null;
  return (
    <span className={`notification-center__severity notification-center__severity--${severity}`}>
      <FiAlertTriangle aria-hidden="true" />
      {t(key)}
    </span>
  );
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const NotificationCenter = ({ id, onClose }) => {
  const { t, language } = useTranslation();
  const { notifications, unreadCount, status, refresh, markRead, markAllRead } =
    useNotifications();
  const [selectedId, setSelectedId] = useState(null);
  const returnFocusId = useRef(null);
  const panelRef = useRef(null);
  const titleId = useId();
  const items = useMemo(() => byNewest(notifications), [notifications]);
  const selected = selectedId
    ? notifications.find((item) => item.id === selectedId) || null
    : null;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (!selected && returnFocusId.current) {
      const row = Array.from(panel.querySelectorAll("[data-notification-id]")).find(
        (element) => element.dataset.notificationId === returnFocusId.current,
      );
      returnFocusId.current = null;
      if (row) {
        row.focus();
        return;
      }
    }
    (panel.querySelector(FOCUSABLE) || panel).focus();
    panel.scrollTop = 0;
    panel.querySelector(".notification-center__body")?.scrollTo?.(0, 0);
  }, [selected]);

  const openItem = (itemId) => {
    setSelectedId(itemId);
    markRead([itemId]);
  };

  const backToList = () => {
    returnFocusId.current = selectedId;
    setSelectedId(null);
  };

  const isLoading = status === NOTIFICATIONS_STATUS.LOADING && !items.length;
  const isError = status === NOTIFICATIONS_STATUS.ERROR && !items.length;

  const renderList = () => {
    if (isLoading) {
      return <p className="notification-center__state" role="status">{t("notifications.loading")}</p>;
    }
    if (isError) {
      return (
        <div className="notification-center__state" role="alert">
          <p>{t("notifications.error")}</p>
          <button type="button" className="notification-center__secondary" onClick={refresh}>
            {t("notifications.retry")}
          </button>
        </div>
      );
    }
    if (!items.length) {
      return <p className="notification-center__state">{t("notifications.empty")}</p>;
    }
    return (
      <ul className="notification-center__list">
        {items.map((item) => {
          const date = formatNotificationDate(item.publishedAt, language);
          return (
            <li key={item.id}>
              <button
                type="button"
                data-notification-id={item.id}
                className={`notification-center__row${item.read ? "" : " is-unread"}`}
                onClick={() => openItem(item.id)}
              >
                <span className="notification-center__row-dot" aria-hidden="true" />
                <span className="notification-center__row-text">
                  <span className="notification-center__row-title">{item.title}</span>
                  <span className="notification-center__row-meta">
                    <SeverityBadge severity={item.severity} t={t} />
                    {date && (
                      <time className="notification-center__row-date" dateTime={item.publishedAt}>
                        {date}
                      </time>
                    )}
                  </span>
                </span>
                {!item.read && (
                  <span className="notification-center__sr-only">{t("notifications.unread")}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const selectedDate = selected ? formatNotificationDate(selected.publishedAt, language) : "";

  return (
    <section
      id={id}
      ref={panelRef}
      className={`notification-dropdown notification-center ${selected ? "is-detail" : "is-list"}`}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex="-1"
    >
      <header className="notification-dropdown__header">
        {selected && (
          <button
            type="button"
            className="notification-dropdown__icon-button"
            aria-label={t("notifications.back")}
            title={t("notifications.back")}
            onClick={backToList}
          >
            <FiArrowLeft aria-hidden="true" />
          </button>
        )}
        <div className="notification-dropdown__heading">
          {selected && (selectedDate || SEVERITY_KEYS[selected.severity]) && (
            <p className="notification-dropdown__meta">
              <SeverityBadge severity={selected.severity} t={t} />
              {selectedDate && <time dateTime={selected.publishedAt}>{selectedDate}</time>}
            </p>
          )}
          <h2 id={titleId}>{selected ? selected.title : t("notifications.title")}</h2>
        </div>
        <button
          type="button"
          className="notification-dropdown__icon-button notification-dropdown__close"
          aria-label={t("notifications.close")}
          title={t("notifications.close")}
          onClick={onClose}
        >
          <FiX aria-hidden="true" />
        </button>
      </header>
      <div
        className={`notification-center__body${selected ? " announcement-modal__body" : ""}`}
      >
        {selected ? <AnnouncementBody blocks={selected.contentBlocks} /> : renderList()}
      </div>
      {!selected && items.length > 0 && (
        <footer className="notification-dropdown__footer">
          <button
            type="button"
            className="notification-dropdown__link"
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            {t("notifications.markAllRead")}
          </button>
        </footer>
      )}
    </section>
  );
};

const NotificationBell = () => {
  const { t } = useTranslation();
  const { unreadCount } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const bellRef = useRef(null);
  const panelId = useId();
  const label = getBellLabel(t, unreadCount);

  const close = useCallback(({ restoreFocus = true } = {}) => {
    setIsOpen(false);
    if (restoreFocus) bellRef.current?.focus();
  }, []);

  // On narrow screens the panel spans the viewport, so we pass the bell's offset to CSS.
  const updatePosition = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const viewport = document.documentElement.clientWidth || window.innerWidth;
    const offset = Math.max(0, viewport - rect.right - 8);
    wrapper.style.setProperty("--notification-dropdown-offset", `${offset}px`);
    wrapper.style.setProperty("--notification-bell-center", `${rect.width / 2}px`);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) close({ restoreFocus: false });
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, close]);

  return (
    <div className="notification-bell-wrapper" ref={wrapperRef}>
      <button
        ref={bellRef}
        type="button"
        className="pill icon-button notification-bell"
        // Opens from the cached list; the dashboard refreshes it in the background.
        onClick={() => setIsOpen((open) => !open)}
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
      >
        <FiBell aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="notification-bell__dot" data-testid="notification-dot" aria-hidden="true" />
        )}
      </button>
      {isOpen && <NotificationCenter id={panelId} onClose={() => close()} />}
    </div>
  );
};

export default NotificationBell;
